import React, { useState } from "react";
import { ArrowRight, Instagram, Music2, Mail, Sparkles } from "lucide-react";
import { brand, heroCopy } from "../mock";
import BookingModal from "./BookingModal";

const Hero = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  return (
    <section id="top" className="relative min-h-screen cosmic-bg overflow-hidden">
      {/* Star field */}
      <div className="stars stars-dense stars-twinkle" />
      <div className="stars" style={{ transform: "translate(20px, 40px)" }} />

      {/* Ambient glows */}
      <div className="pointer-events-none absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-[#A36CFF]/20 blur-[120px]" />
      <div className="pointer-events-none absolute top-40 -right-32 w-[620px] h-[620px] rounded-full bg-[#D4A85B]/12 blur-[140px]" />

      {/* Orbits behind portrait */}
      <div className="pointer-events-none absolute right-[-6%] top-1/2 -translate-y-1/2 w-[720px] h-[720px] hidden lg:block">
        <div className="absolute inset-0 gold-ring animate-orbit-slow" />
        <div className="absolute inset-10 gold-ring opacity-70 animate-orbit-reverse" />
        <div className="absolute inset-24 gold-ring opacity-40 animate-orbit" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-36 md:pt-44 pb-24 grid md:grid-cols-12 gap-14 items-center">
        <div className="md:col-span-7 relative z-10">
          <div className="reveal divider-star">
            <Sparkles size={14} className="text-[#D4A85B]" />
            <span>{heroCopy.eyebrow}</span>
          </div>

          <h1 className="reveal reveal-delay-1 font-serif mt-8 text-[52px] md:text-[86px] leading-[0.98] text-[#EDE3D0]">
            <span className="block">
              Heal your <em className="not-italic font-serif italic text-[#D4A85B]">mind.</em>
            </span>
            <span className="block mt-1">
              Elevate your{" "}
              <em className="not-italic font-serif italic text-[#D4A85B]">spirit.</em>
            </span>
            <span className="block mt-1">
              Transform your{" "}
              <em className="not-italic font-serif italic text-[#D4A85B]">life.</em>
            </span>
          </h1>

          <p className="reveal reveal-delay-2 mt-8 max-w-xl text-lg leading-relaxed text-[#EDE3D0]/80">
            {heroCopy.body}
          </p>

          <div className="reveal reveal-delay-3 mt-10 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => setBookingOpen(true)}
              className="group inline-flex items-center gap-2 rounded-full bg-[#D4A85B] text-[#0A0714] px-7 py-3.5 text-xs uppercase tracking-[0.28em] hover:bg-[#E9C77B] transition"
            >
              Begin your journey
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href="#philosophy"
              className="inline-flex items-center gap-2 rounded-full border border-[#D4A85B]/60 text-[#EDE3D0] px-7 py-3.5 text-xs uppercase tracking-[0.28em] hover:border-[#D4A85B] hover:text-[#D4A85B] transition"
            >
              The philosophy
            </a>
          </div>

          <div className="reveal reveal-delay-4 mt-12 flex items-center gap-5">
            <span className="text-[10px] uppercase tracking-[0.32em] text-[#D4A85B]/70">Follow</span>
            <a href={brand.instagram} className="w-10 h-10 rounded-full border border-[#D4A85B]/40 flex items-center justify-center text-[#EDE3D0] hover:bg-[#D4A85B] hover:text-[#0A0714] transition">
              <Instagram size={15} />
            </a>
            <a href={brand.tiktok} className="w-10 h-10 rounded-full border border-[#D4A85B]/40 flex items-center justify-center text-[#EDE3D0] hover:bg-[#D4A85B] hover:text-[#0A0714] transition">
              <Music2 size={15} />
            </a>
            <a href={`mailto:${brand.email}`} className="w-10 h-10 rounded-full border border-[#D4A85B]/40 flex items-center justify-center text-[#EDE3D0] hover:bg-[#D4A85B] hover:text-[#0A0714] transition">
              <Mail size={15} />
            </a>
          </div>
        </div>

        {/* Portrait with celestial frame */}
        <div className="md:col-span-5 relative z-10">
          <div className="reveal reveal-delay-2 relative mx-auto max-w-sm animate-floaty">
            {/* Outer gold aura ring */}
            <div className="absolute -inset-6 rounded-[36px] border border-[#D4A85B]/40 pulse-glow" />
            <div className="absolute -inset-14 rounded-[48px] border border-[#D4A85B]/15" />
            {/* Portrait */}
            <div className="relative aspect-square rounded-[32px] overflow-hidden border border-[#D4A85B]/40 shadow-[0_30px_80px_-20px_rgba(212,168,91,0.35)] bg-gradient-to-b from-[#1A0E30] to-[#0A0714]">
              <img
                src={brand.profileImage}
                alt="Devona Nicole Hazelwood"
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0714] via-transparent to-transparent opacity-40 pointer-events-none" />
            </div>
            {/* Name plate */}
            <div className="relative mt-6 text-center">
              <div className="font-serif text-2xl text-[#EDE3D0]">{brand.founder}</div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]">
                {brand.credentials}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.42em] text-[#D4A85B]/70">
        Scroll to descend deeper
      </div>

      <BookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} />
    </section>
  );
};

export default Hero;
